import {
  type Appointment,
  type AppointmentSettings,
  type AppointmentStatus
} from '../model/appointment'

const DEFAULT_TIMEZONE = 'UTC'

function validTimeZone(timezone: string): string {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format()
    return timezone
  } catch {
    return DEFAULT_TIMEZONE
  }
}

export function detectedTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE
}

export function defaultAppointmentSettings(
  timezone = detectedTimeZone()
): AppointmentSettings {
  return {
    timezone: validTimeZone(timezone),
    defaultDuration: 30,
    weeklyHours: {
      0: { enabled: true, start: '09:00', end: '17:00' },
      1: { enabled: true, start: '09:00', end: '17:00' },
      2: { enabled: true, start: '09:00', end: '17:00' },
      3: { enabled: true, start: '09:00', end: '17:00' },
      4: { enabled: true, start: '09:00', end: '17:00' },
      5: { enabled: false, start: '09:00', end: '17:00' },
      6: { enabled: true, start: '09:00', end: '17:00' }
    }
  }
}

export function normalizeAppointmentSettings(
  settings?: Partial<AppointmentSettings>
): AppointmentSettings {
  const defaults = defaultAppointmentSettings(settings?.timezone)
  return {
    ...defaults,
    ...settings,
    timezone: validTimeZone(settings?.timezone ?? defaults.timezone),
    weeklyHours: {
      ...defaults.weeklyHours,
      ...(settings?.weeklyHours ?? {})
    }
  }
}

interface ZonedParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

export function getZonedParts(timestamp: number, timezone: string): ZonedParts {
  const values: Record<string, string> = {}
  new Intl.DateTimeFormat('en-CA', {
    timeZone: validTimeZone(timezone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  })
    .formatToParts(new Date(timestamp))
    .forEach((part) => {
      if (part.type !== 'literal') values[part.type] = part.value
    })

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second)
  }
}

export function dateKeyInTimeZone(timestamp: number, timezone: string): string {
  const parts = getZonedParts(timestamp, timezone)
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(
    parts.day
  ).padStart(2, '0')}`
}

export function timestampToClinicInput(
  timestamp: number,
  timezone: string
): { date: string; time: string } {
  const parts = getZonedParts(timestamp, timezone)
  return {
    date: `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(
      parts.day
    ).padStart(2, '0')}`,
    time: `${String(parts.hour).padStart(2, '0')}:${String(
      parts.minute
    ).padStart(2, '0')}`
  }
}

export function clinicDateTimeToTimestamp(
  date: string,
  time: string,
  timezone: string
): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) {
    throw new RangeError('Invalid clinic date or time')
  }
  const [year, month, day] = date.split('-').map(Number)
  const [hour, minute] = time.split(':').map(Number)
  const target = Date.UTC(year, month - 1, day, hour, minute)
  const targetDate = new Date(target)
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    targetDate.getUTCFullYear() !== year ||
    targetDate.getUTCMonth() !== month - 1 ||
    targetDate.getUTCDate() !== day
  ) {
    throw new RangeError('Invalid clinic date or time')
  }
  let guess = target

  // Reconcile the requested wall clock with the timezone offset. Repeating
  // handles DST boundaries without requiring a timezone library.
  for (let iteration = 0; iteration < 3; iteration += 1) {
    const parts = getZonedParts(guess, timezone)
    const observed = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute
    )
    guess += target - observed
  }

  const resolved = getZonedParts(guess, timezone)
  if (
    resolved.year !== year ||
    resolved.month !== month ||
    resolved.day !== day ||
    resolved.hour !== hour ||
    resolved.minute !== minute
  ) {
    throw new RangeError('Invalid clinic date or time')
  }
  return guess
}

export function timestampToCalendarDate(
  timestamp: number,
  timezone: string
): Date {
  const parts = getZonedParts(timestamp, timezone)
  // FullCalendar renders these wall-clock components in UTC. Using UTC here
  // prevents the browser's own DST rules from shifting a valid clinic time.
  return new Date(
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    )
  )
}

export const ACTIVE_APPOINTMENT_STATUSES: AppointmentStatus[] = [
  'scheduled',
  'confirmed',
  'checked-in'
]

const STATUS_TRANSITIONS: Record<AppointmentStatus, AppointmentStatus[]> = {
  scheduled: ['confirmed', 'checked-in', 'cancelled', 'no-show'],
  confirmed: ['scheduled', 'checked-in', 'cancelled', 'no-show'],
  'checked-in': ['scheduled', 'completed', 'cancelled'],
  completed: [],
  cancelled: ['scheduled'],
  'no-show': ['scheduled']
}

export function canTransitionAppointment(
  from: AppointmentStatus,
  to: AppointmentStatus
): boolean {
  return from === to || STATUS_TRANSITIONS[from].includes(to)
}

export function appointmentsOverlap(
  start: number,
  duration: number,
  candidate: Pick<Appointment, 'start' | 'duration' | 'status'>
): boolean {
  if (!ACTIVE_APPOINTMENT_STATUSES.includes(candidate.status)) return false
  const end = start + duration * 60_000
  const candidateEnd = candidate.start + candidate.duration * 60_000
  return start < candidateEnd && end > candidate.start
}

export function validateAppointmentSlot(
  start: number,
  duration: number,
  settings: AppointmentSettings
): string | null {
  const parts = getZonedParts(start, settings.timezone)
  const dayHours =
    settings.weeklyHours[
      new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()
    ]
  if (!dayHours?.enabled) return 'closed-day'

  const minuteOfDay = parts.hour * 60 + parts.minute
  const [openHour, openMinute] = dayHours.start.split(':').map(Number)
  const [closeHour, closeMinute] = dayHours.end.split(':').map(Number)
  const opening = openHour * 60 + openMinute
  const closing = closeHour * 60 + closeMinute
  if (minuteOfDay < opening || minuteOfDay + duration > closing) {
    return 'outside-hours'
  }
  return null
}
